import dayjs from "dayjs";

import type {
  FilterDefinition,
  ListQueryCodecConfig,
  ListQueryState,
} from "./types";
import { parseListQueryState, serializeListQueryState } from "./listQuery";

export type FlightListFilters = {
  date: string | null;
  dateFrom: string | null;
  dateTo: string | null;
  callsign: string | null;
  registration: string | null;
  departureAirport: string | null;
  arrivalAirport: string | null;
  flightID: string | null;
  commercialFlightNumber: string | null;
  tlc: string | null;
  atcType:string|null; flightPurpose:string|null; purposeReview:string|null;
  operatorRef: number | null;
};

export const DEFAULT_FLIGHT_LIST_FILTERS: FlightListFilters = {
  date: null,
  dateFrom: null,
  dateTo: null,
  callsign: null,
  registration: null,
  departureAirport: null,
  arrivalAirport: null,
  flightID: null,
  commercialFlightNumber: null,
  tlc: null,
  atcType:null, flightPurpose:null, purposeReview:null,
  operatorRef: null,
};

const normalizeText = (value: string | number | null): string | null => {
  if (typeof value !== "string") {
    return null;
  }

  return value.trim() || null;
};

const normalizeDate = (value: string | number | null): string | null => {
  const normalized = normalizeText(value);
  if (!normalized || !/^\d{4}-\d{2}-\d{2}$/.test(normalized)) {
    return null;
  }

  const parsed = dayjs(normalized);
  return parsed.isValid() && parsed.format("YYYY-MM-DD") === normalized
    ? normalized
    : null;
};

const parseText = (values: string[]) => values[0] || null;
const serializeText = (value: string | number | null) => {
  const normalized = normalizeText(value);
  return normalized ? [normalized] : [];
};
const isTextActive = (value: string | number | null) => Boolean(normalizeText(value));

const textFilter = (
  key: Extract<keyof FlightListFilters, string>,
  label: string,
): FilterDefinition<FlightListFilters> => ({
  key,
  label,
  inputType: "single-select",
  defaultValue: null,
  parse: parseText,
  serialize: serializeText,
  normalize: normalizeText,
  isActive: isTextActive,
  toChipLabel: (value) => `${label}: ${normalizeText(value) || ""}`,
});

const dateFilter = (
  key: "date" | "dateFrom" | "dateTo",
  label: string,
): FilterDefinition<FlightListFilters> => ({
  key,
  label,
  inputType: key === "date" ? "date" : "date-range",
  defaultValue: null,
  parse: parseText,
  serialize: (value) => {
    const normalized = normalizeDate(value);
    return normalized ? [normalized] : [];
  },
  normalize: normalizeDate,
  isActive: (value) => Boolean(normalizeDate(value)),
  toChipLabel: (value) => `${label}: ${normalizeDate(value) || ""}`,
});

const operatorFilter: FilterDefinition<FlightListFilters> = {
  key: "operatorRef",
  label: "Operator",
  inputType: "single-select",
  defaultValue: null,
  parse: (values) => Number.parseInt(values[0] || "", 10) || null,
  serialize: (value) => typeof value === "number" && value > 0 ? [`${value}`] : [],
  normalize: (value) => {
    const parsed = Number.parseInt(`${value ?? ""}`, 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  },
  isActive: (value) => typeof value === "number" && value > 0,
  toChipLabel: (value) => `Operator: ${value || ""}`,
};

export const flightListFilterDefinitions: FilterDefinition<FlightListFilters>[] = [
  dateFilter("date", "Date"),
  dateFilter("dateFrom", "From"),
  dateFilter("dateTo", "To"),
  textFilter("callsign", "Callsign"),
  textFilter("registration", "Registration"),
  textFilter("departureAirport", "Departure"),
  textFilter("arrivalAirport", "Arrival"),
  textFilter("flightID", "Flight ID"),
  textFilter("commercialFlightNumber", "Commercial"),
  textFilter("tlc", "TLC"),
  textFilter("atcType", "Flight type"), textFilter("flightPurpose", "Flight purpose"), textFilter("purposeReview", "Purpose review"),
  operatorFilter,
];

export const flightListQueryConfig: ListQueryCodecConfig<FlightListFilters> = {
  filters: flightListFilterDefinitions,
  defaultFilters: DEFAULT_FLIGHT_LIST_FILTERS,
  defaultSort: null,
  defaultPagination: { mode: "cursor" },
  searchParameter: "search",
};

const serializeDate = (value: Filters["date"]): string | null =>
  value && typeof value.format === "function" ? value.format("YYYY-MM-DD") : null;

const parseDate = (value: string | null) => value ? dayjs(value) : null;

export const toFlightListQueryState = (filters: Filters): ListQueryState<FlightListFilters> => ({
  search: `${filters.search || ""}`,
  filters: {
    date: serializeDate(filters.date),
    dateFrom: serializeDate(filters.dateFrom || null),
    dateTo: serializeDate(filters.dateTo || null),
    callsign: normalizeText(filters.callsign),
    registration: normalizeText(filters.registration),
    departureAirport: normalizeText(filters.departureAirport),
    arrivalAirport: normalizeText(filters.arrivalAirport),
    flightID: normalizeText(filters.flightID),
    commercialFlightNumber: normalizeText(filters.commercialFlightNumber),
    tlc: normalizeText(filters.tlc),
    atcType:normalizeText(filters.atcType||null), flightPurpose:normalizeText(filters.flightPurpose||null), purposeReview:normalizeText(filters.purposeReview||null),
    operatorRef: operatorFilter.normalize(filters.operatorRef || null) as number | null,
  },
  sort: null,
  pagination: { mode: "cursor" },
});

export const fromFlightListQueryState = (
  state: ListQueryState<FlightListFilters>,
  isSuperAdmin: boolean,
): Filters => ({
  date: parseDate(state.filters.date),
  dateFrom: parseDate(state.filters.dateFrom),
  dateTo: parseDate(state.filters.dateTo),
  search: state.search,
  callsign: state.filters.callsign,
  registration: state.filters.registration,
  departureAirport: state.filters.departureAirport,
  arrivalAirport: state.filters.arrivalAirport,
  flightID: state.filters.flightID,
  commercialFlightNumber: state.filters.commercialFlightNumber,
  tlc: state.filters.tlc,
  atcType:state.filters.atcType, flightPurpose:state.filters.flightPurpose, purposeReview:state.filters.purposeReview,
  operatorRef: isSuperAdmin ? state.filters.operatorRef : null,
});

const managedFlightParameters = new Set([
  "search",
  ...flightListFilterDefinitions.map((definition) => definition.parameter || definition.key),
]);

export const hasManagedFlightListParameters = (params: URLSearchParams): boolean =>
  Array.from(managedFlightParameters).some((parameter) => params.has(parameter));

export const parseFlightFiltersFromUrl = (
  params: URLSearchParams,
  isSuperAdmin: boolean,
): Filters => {
  const state = parseListQueryState(params, flightListQueryConfig);
  if (!isSuperAdmin) {
    state.filters.operatorRef = null;
  }
  return fromFlightListQueryState(state, isSuperAdmin);
};

export const serializeFlightFiltersToUrl = (
  filters: Filters,
  isSuperAdmin: boolean,
  currentParams: URLSearchParams = new URLSearchParams(),
): URLSearchParams => {
  const state = toFlightListQueryState(filters);
  if (!isSuperAdmin) {
    state.filters.operatorRef = null;
  }
  return serializeListQueryState(state, flightListQueryConfig, currentParams);
};

export const hydrateFlightFilters = (
  source: Partial<Filters> | Record<string, unknown>,
  isSuperAdmin: boolean,
): Filters => {
  const params = new URLSearchParams();
  const search = normalizeText(typeof source.search === "string" ? source.search : null);
  if (search) {
    params.set("search", search);
  }

  flightListFilterDefinitions.forEach((definition) => {
    if (definition.key === "operatorRef" && !isSuperAdmin) {
      return;
    }
    const value = source[definition.key as keyof typeof source] as string | number | null;
    definition.serialize(definition.normalize(value)).forEach((entry) => {
      params.append(definition.parameter || definition.key, entry);
    });
  });

  return parseFlightFiltersFromUrl(params, isSuperAdmin);
};

export const serializeFlightFiltersForSavedView = (
  filters: Filters,
  isSuperAdmin: boolean,
): Record<string, string | number> => {
  const params = serializeFlightFiltersToUrl(filters, isSuperAdmin);
  const payload: Record<string, string | number> = {};

  params.forEach((value, key) => {
    payload[key] = key === "operatorRef" ? Number.parseInt(value, 10) : value;
  });

  return payload;
};

export const countActiveFlightFilters = (filters: Filters, isSuperAdmin: boolean): number =>
  Object.keys(serializeFlightFiltersForSavedView(filters, isSuperAdmin)).length;
